import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import AddButton from "components/AddButton";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import EditButton from "components/EditButton";
import MaterialTableTS from "components/tables/MaterialTableTS";
import { toast } from "components/toastNotification";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { PERMISSIONS } from "modules/PermissionsConstants";
import useDeleteOperationsGroup from "../hooks/useDeleteOperationsGroup";
import useGetOperationsGroupList from "../hooks/useGetOperationsGroup";
import {
  addStateOperationsGroup,
  operationsGroupFetching,
  operationsGroupFilterText,
  operationsGroupReFetching,
  editStateOperationsGroup,
  rowEditOperationsGroup,
  selectedRowsStateOperationsGroup,
} from "../atoms/atoms";
import { IOperationsGroup, IOperationsGroupTableData } from "../types";
import Timer from "components/Timer";
import TableFilters from "components/TableFilters";
import OperationsGroupForm from "../form/OperationsGroupForm";
import { UserGroupIcon } from "@heroicons/react/24/solid";

export const OperationsGroupTable = ({}) => {
  const { data: directData, refetch: reFetch } = useGetOperationsGroupList();

  const BulkDelete = useDeleteOperationsGroup();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(
    addStateOperationsGroup
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateOperationsGroup
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateOperationsGroup
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    operationsGroupFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditOperationsGroup);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(operationsGroupFetching);
  const isReFetching = useRecoilValue(operationsGroupReFetching);

  //Local States
  const [tableData, setTableData] = useState<IOperationsGroup[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<
    IOperationsGroup[]
  >([]);

  // Permission States
  const addPermission = PERMISSIONS.permission_utilities_operationsgroup_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_operationsgroup_view;
  const editPermission = PERMISSIONS.permission_utilities_operationsgroup_edit;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  // Get Data
  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status == 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: IOperationsGroup[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].operationName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  //For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.operationName &&
        item.operationName
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );
  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: IOperationsGroupTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: IOperationsGroupTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.operationId);
    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Operations Groups" : "Operations Group"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Operations Group Name",
      field: "operationName",
      customSort: (a, b) =>
        a.operationName && b.operationName
          ? a.operationName.trim().localeCompare(b.operationName.trim())
          : a.operationName
          ? 0
          : -1,
    },
    {
      title: "Description",
      field: "description",
      customSort: (a, b) =>
        a.description && b.description
          ? a.description.trim().localeCompare(b.description.trim())
          : a.description
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white mx-8 py-4">
      {!(actionAddState || actionEditState) && (
        <div>
          <div className="flex gap-2 items-center">
            <div>
              <UserGroupIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
            </div>
            <div className="flex flex-col leading tight">
              <h3 className="text-xs font-medium text-gray-500">
                Utilities Module
              </h3>
              <h1 className="mr-auto font-bold text-primary text-md uppercase">
                Operations Group Configuration
              </h1>
            </div>
          </div>
          <div className="h-0.5 w-full border-b-2 border-secondary mt-2 mb-4" />
        </div>
      )}
      {actionAddState || actionEditState ? (
        <div>
          <OperationsGroupForm setRecent={setRecent} />
        </div>
      ) : (
        <div className="">
          <div className="flex w-full">
            <div className="bg-white w-full">
              <div className="flex justify-between text-xs items-center w-full">
                <div className="">
                  <TableFilters
                    defaultText={filterTextState}
                    textInputName={"OperationsGroupName"}
                    columnDetails={"Operations Group Name"}
                    filterText={setFilterTextState}
                    recentText={recent}
                    setRecent={setRecent}
                    disabledButton={
                      actionEditState ||
                      (selectedRows !== undefined && selectedRows.length > 0)
                    }
                    disabledTextBox={
                      selectedRows !== undefined && selectedRows.length > 0
                    }
                  />
                </div>
                <div className="">
                  {userPermissions.includes(addPermission) ? (
                    <AddButton
                      buttonText="New Operations Group"
                      onClick={() => handleAdd()}
                      disabled={
                        actionEditState ||
                        (selectedRows !== undefined && selectedRows.length > 0)
                      }
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>

              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}

              <div className="">
                <MaterialTableTS
                  data={
                    isFetching
                      ? []
                      : filterTextState === "" && recent === ""
                      ? tableData
                      : filterTextState === ""
                      ? tableFilteredData
                      : filtereditems
                  }
                  showTitle={true}
                  columns={matColumns}
                  isLoading={isFetching}
                  onSelectionChange={handleSelectedRowsChange}
                  selectedRows={selectedRows}
                  selection={
                    !actionEditState &&
                    !actionAddState &&
                    userPermissions.includes(deletePermission)
                  }
                  setConfirmModalOpen={setOpenModalDelete}
                />
              </div>
            </div>
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Operations Group"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </div>
      )}
    </div>
  );
};

export default OperationsGroupTable;
